import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/common.css';
import '../../styles/test-plans.css';
import axiosInstance from '../../api/axiosInstance';
import { AccessibleSelect } from '../common';
import { formatDateTime } from '../../utils/dateTimeFormat';
import { useProject } from '../../context/ProjectContext';
import { RtModal, RtModalField, RtModalSection } from '../ui/RtModal';
import NotificationEmailSelect from './NotificationEmailSelect';

export const ScheduleType = {
  ONCE: 'ONCE',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  CUSTOM: 'CUSTOM'
};

const SCHEDULE_TYPE_OPTIONS = [
  { value: ScheduleType.ONCE, label: 'Once' },
  { value: ScheduleType.DAILY, label: 'Daily' },
  { value: ScheduleType.WEEKLY, label: 'Weekly' },
  { value: ScheduleType.MONTHLY, label: 'Monthly' },
  { value: ScheduleType.CUSTOM, label: 'Custom' },
];

const getDefaultFormData = (plan = null) => ({
  test_plan_id: plan?.test_plan_id || '',
  test_plan_name: plan?.name || '',
  schedule_name: '',
  date: '',
  time: '',
  schedule_type: ScheduleType.ONCE,
  notification_emails: [],
  comment: ''
});


const ScheduleTestPlanModal = ({
  isOpen,
  onClose,
  testPlanList = [],
  initialPlan = null,
  projectId,
  onFeedback
}) => {
  const { selectedProject } = useProject();
  const resolvedProjectId = projectId || initialPlan?.project_id || selectedProject?.id;
  const [formData, setFormData] = useState(getDefaultFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const testPlanOptions = useMemo(
    () =>
      testPlanList.map((plan) => ({
        value: String(plan.test_plan_id),
        label: plan.name,
      })),
    [testPlanList]
  );

  const showFeedback = (message, type = 'success') => {
    if (onFeedback) {
      onFeedback(message, type);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData(getDefaultFormData(initialPlan));
    }
  }, [isOpen, initialPlan]);

  const handleClose = () => {
    setFormData(getDefaultFormData());
    onClose();
  };

  const handleTestPlanChange = (value) => {
    const selectedPlan = testPlanList.find(
      (plan) => String(plan.test_plan_id) === String(value)
    );
    setFormData((prev) => ({
      ...prev,
      test_plan_id: value,
      test_plan_name: selectedPlan?.name || '',
    }));
  };

  const isFormValid =
    Boolean(formData.test_plan_id) &&
    Boolean(formData.schedule_name?.trim()) &&
    Boolean(formData.date) &&
    Boolean(formData.time);

  const handleSchedule = async () => {
    if (!isFormValid) {
      showFeedback('Please fill in all required fields (Test Plan, Schedule Name, Date, and Time)', 'error');
      return;
    }

    const localDateTime = new Date(`${formData.date}T${formData.time}:00`);
    if (Number.isNaN(localDateTime.getTime())) {
      showFeedback('Invalid date or time', 'error');
      return;
    }

    const combinedDateTime = localDateTime.toISOString();
    const payload = {
      test_plan_id: Number(formData.test_plan_id),
      schedule_name: formData.schedule_name.trim(),
      schedule_type: formData.schedule_type,
      run_time: combinedDateTime,
      comments: formData.comment,
      notification_emails: formData.notification_emails
    };

    try {
      setIsSubmitting(true);
      const response = await axiosInstance.post('/execute_file/schedule-plan', payload);

      if (response.status == 'success') {
        const scheduledDate = new Date(response.scheduled_for);
        showFeedback(`Test Execution scheduled for ${formatDateTime(scheduledDate)}`, 'success');
        handleClose();
      } else {
        throw new Error(response.data?.message || 'Failed to schedule test plan');
      }
    } catch (error) {
      console.error('Error scheduling test plan:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage =
        (typeof errorDetail === 'string' && errorDetail) ||
        error.response?.data?.message ||
        'Failed to schedule test plan';
      showFeedback(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RtModal
      open={isOpen}
      onClose={handleClose}
      title="Schedule Test Plan"
      subtitle="Choose when this plan should run and who should be notified."
      size="lg"
      closeDisabled={isSubmitting}
      footer={
        <>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--primary"
            onClick={handleSchedule}
            disabled={isSubmitting || !isFormValid}
            title={
              !isFormValid
                ? 'Fill in Test Plan, Schedule Name, Date, and Time to schedule'
                : undefined
            }
          >
            {isSubmitting ? 'Scheduling…' : 'Schedule'}
          </button>
        </>
      }
    >
      <RtModalSection title="Plan & cadence">
        <div className="rt-modal__grid-2">
          <RtModalField label="Test Plan" htmlFor="testPlanSelect" required>
            <AccessibleSelect
              id="testPlanSelect"
              value={formData.test_plan_id}
              onChange={handleTestPlanChange}
              options={testPlanOptions}
              placeholder="Select a test plan"
              required
              disabled={isSubmitting}
              aria-labelledby="testPlanSelect-label"
            />
          </RtModalField>
          <RtModalField label="Schedule Type" htmlFor="scheduleType">
            <AccessibleSelect
              id="scheduleType"
              value={formData.schedule_type}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, schedule_type: value }))
              }
              options={SCHEDULE_TYPE_OPTIONS}
              placeholder="Select schedule type"
              disabled={isSubmitting}
            />
          </RtModalField>
          <RtModalField label="Schedule Name" htmlFor="scheduleName" required>
            <input
              type="text"
              id="scheduleName"
              value={formData.schedule_name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, schedule_name: e.target.value }))
              }
              placeholder="Enter schedule name"
              required
              disabled={isSubmitting}
            />
          </RtModalField>
          <RtModalField label="Notification Emails" htmlFor="notificationEmails">
            <NotificationEmailSelect
              id="notificationEmails"
              projectId={resolvedProjectId}
              value={formData.notification_emails}
              onChange={(emails) =>
                setFormData((prev) => ({ ...prev, notification_emails: emails }))
              }
              disabled={isSubmitting}
            />
          </RtModalField>
        </div>
      </RtModalSection>

      <RtModalSection title="Run time">
        <div className="rt-modal__grid-2">
          <RtModalField label="Date" htmlFor="scheduleDate" required>
            <input
              type="date"
              id="scheduleDate"
              value={formData.date}
              onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
              min={new Date().toISOString().split('T')[0]}
              required
              disabled={isSubmitting}
            />
          </RtModalField>
          <RtModalField label="Time (24-hour)" htmlFor="scheduleTime" required>
            <input
              type="time"
              id="scheduleTime"
              value={formData.time}
              onChange={(e) => setFormData((prev) => ({ ...prev, time: e.target.value }))}
              required
              disabled={isSubmitting}
            />
          </RtModalField>
        </div>
        <RtModalField label="Comment" htmlFor="comment">
          <textarea
            id="comment"
            value={formData.comment}
            onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
            placeholder="Add any additional notes about this scheduled run…"
            rows={4}
            disabled={isSubmitting}
          />
        </RtModalField>
      </RtModalSection>
    </RtModal>
  );
};

export default ScheduleTestPlanModal;
